/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPlus, AlertCircle, CheckCircle, Building2, User, GraduationCap, Landmark, ExternalLink } from 'lucide-react';
import PasswordInput, { ConfirmPasswordInput } from '../components/auth/PasswordInput';
import TurnstileGate from '../components/security/TurnstileGate';
import useCaptchaConfig from '../hooks/useCaptchaConfig';
import api from '../services/api';

// Entity type configuration
const ENTITY_TYPES = [
  { value: 'individual', label: 'Individual', icon: User, description: 'Personal learning account' },
  { value: 'organization', label: 'Organization', icon: Building2, description: 'Business or corporate training' },
  { value: 'institute', label: 'Institute', icon: GraduationCap, description: 'Educational institution' },
  { value: 'university', label: 'University', icon: Landmark, description: 'Higher education university' }
];

// Age gating (DPDP): compute integer years between DOB and today, UTC-safe.
const computeAgeYears = (isoDob) => {
  if (!isoDob) return null;
  const d = new Date(isoDob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getUTCFullYear() - d.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - d.getUTCMonth();
  const dayDelta = now.getUTCDate() - d.getUTCDate();
  if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0)) years -= 1;
  return years;
};

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'learner',
    vertical: 'WMS',
    dateOfBirth: '',
    // V2.0 Fields
    entityType: 'individual',
    organizationName: '',
    subCategoryId: '',
    tcAccepted: false
  });

  // Max selectable DOB = today (browsers still allow past dates freely).
  const todayIso = new Date().toISOString().slice(0, 10);
  const ageYears = computeAgeYears(formData.dateOfBirth);
  const dobPresent = formData.dateOfBirth !== '';
  const dobIsAdult = dobPresent && ageYears !== null && ageYears >= 18;
  const dobIsMinor = dobPresent && ageYears !== null && ageYears < 18;
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { register } = useAuth();

  // V2.0: T&C and sub-categories state
  const [tcVersion, setTcVersion] = useState(null);
  const [subCategories, setSubCategories] = useState({});
  // Default vertical list — Travel is appended by a debounced lookup
  // against /api/public/verticals when the typed email qualifies.
  const [verticalOptions, setVerticalOptions] = useState([
    { value: 'WMS',       label: 'Warehouse Management' },
    { value: 'iSeries',   label: 'IBM i / AS400' },
    { value: 'Civil',     label: 'Civil Construction' },
    { value: 'Stitching', label: 'Apparel & Textiles' },
    { value: 'ERPNext',   label: 'ERPNext' },
    { value: 'SkillCode', label: 'SkillCode — AI Engineering Intelligence' }
  ]);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaStatus, setCaptchaStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const captchaRef = useRef(null);
  const captchaCfg = useCaptchaConfig();
  const needsCaptcha = captchaCfg.ready && captchaCfg.captchaEnabled;
  const captchaBlocks = needsCaptcha && (!captchaToken || captchaStatus !== 'ready');
  const [loadingMeta, setLoadingMeta] = useState(true);

  // V2.0: Load T&C version and sub-categories on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        // Load T&C version
        const tcRes = await api.get('/auth/current-tc');
        if (tcRes.data.success) {
          setTcVersion(tcRes.data.data);
        }

        // Load sub-categories
        const subCatRes = await api.get('/auth/entity-sub-categories');
        if (subCatRes.data.success) {
          setSubCategories(subCatRes.data.data);
        }
      } catch (err) {
        console.error('Failed to load registration metadata:', err);
      } finally {
        setLoadingMeta(false);
      }
    };

    loadMetadata();
  }, []);

  // ShubbhYatra: debounced lookup — 'Travel' is appended to the dropdown
  // only when the typed email ends @shubbhyatra.com or matches a pending
  // rep invite. Byte-identical UI for every other email.
  useEffect(() => {
    const email = (formData.email || '').trim().toLowerCase();
    const handle = setTimeout(async () => {
      try {
        const res = await api.get(`/public/verticals?email=${encodeURIComponent(email)}`);
        const list = res?.data?.data?.verticals;
        if (Array.isArray(list) && list.length) {
          setVerticalOptions(list.map((v) => ({ value: v.value, label: v.label })));
          // Snap back to a safe default if the user had Travel selected
          // but the current email no longer qualifies.
          if (!list.some((v) => v.value === 'Travel') && formData.vertical === 'Travel') {
            setFormData((prev) => ({ ...prev, vertical: 'WMS' }));
          }
        }
      } catch (err) {
        // On error, leave the default list in place. Never surface noise.
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [formData.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // V2.0: Check if organization name is required
  const requiresOrgName = formData.entityType !== 'individual';

  // Check if form is valid for submission
  const isFormValid =
    formData.fullName.trim() !== '' &&
    formData.email.trim() !== '' &&
    passwordValidation.isValid &&
    formData.password === formData.confirmPassword &&
    formData.confirmPassword !== '' &&
    dobIsAdult &&
    // V2.0 Validation
    formData.tcAccepted &&
    (!requiresOrgName || formData.organizationName.trim() !== '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password matches
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (!passwordValidation.isValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    // Age gating (DPDP): block under-18 client-side before we call the API.
    if (!dobPresent || ageYears === null) {
      setError('Please enter your date of birth.');
      return;
    }
    if (dobIsMinor) {
      setError('You must be 18 or older to create a SkillDelivery account.');
      return;
    }

    // V2.0: Validate T&C acceptance
    if (!formData.tcAccepted) {
      setError('You must accept the Terms & Conditions to register');
      return;
    }

    // V2.0: Validate organization name
    if (requiresOrgName && !formData.organizationName.trim()) {
      setError('Organization name is required');
      return;
    }

    setLoading(true);

    try {
      // Captcha (Cloudflare Turnstile) — required by server on web signup
      // when CAPTCHA_ENABLED. Skipped entirely when the kill switch is off.
      if (needsCaptcha && !captchaToken) {
        setError('Please complete the security check.');
        setLoading(false);
        return;
      }
      // V2.0: Extended registration with entity data
      const result = await register(
        formData.email,
        formData.password,
        formData.fullName,
        formData.userType,
        formData.vertical,
        // V2.0 fields
        {
          entityType: formData.entityType,
          organizationName: formData.organizationName,
          subCategoryId: formData.subCategoryId || null,
          tcAccepted: formData.tcAccepted,
          dateOfBirth: formData.dateOfBirth,
          captchaToken
        }
      );
      // Consume the token whether or not registration succeeded.
      setCaptchaToken(null);
      if (captchaRef.current) captchaRef.current.reset();

      if (result.success) {
        // Show success message - user needs to verify email
        setRegistrationSuccess(true);
        // Clear form
        setFormData({
          fullName: '',
          email: '',
          password: '',
          confirmPassword: '',
          userType: 'learner',
          vertical: 'WMS',
          dateOfBirth: '',
          entityType: 'individual',
          organizationName: '',
          subCategoryId: '',
          tcAccepted: false
        });
        // No auto-redirect - user must verify email first
      } else {
        // Handle specific error cases
        if (result.error?.includes('already exists')) {
          setError('An account with this email already exists. Please sign in instead.');
        } else if (result.details && Array.isArray(result.details)) {
          setError(result.details.join('. '));
        } else {
          setError(result.error || 'Registration failed. Please try again.');
        }
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    }

    setLoading(false);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-brand-bgOff via-white to-white flex items-center justify-center p-4">

      {/* ── Decorative radial glows ── */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32"
        style={{
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(92, 225, 230, 0.15) 0%, transparent 70%)'
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24"
        style={{
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, transparent 70%)'
        }}
      />

      <div className="relative bg-white rounded-brandCard shadow-brand border border-brand-border p-8 w-full max-w-md">

        {/* ── Logo + wordmark ── */}
        <div className="flex items-center justify-center gap-2.5 mb-2">
          <img src="/logo.jpg" alt="SkillDelivery" className="h-9 w-auto" />
          <span className="font-bold text-xl tracking-tight text-brand-text">
            Skill<span className="text-brand-cyan">Delivery</span>
          </span>
        </div>
        <p className="text-center text-sm text-brand-muted mb-6">Skills, Delivered!</p>

        <h2 className="text-2xl font-bold text-center text-brand-text mb-2">
          Create Account
        </h2>
        <p className="text-center text-sm text-brand-muted mb-6">
          Start your skill development journey
        </p>

        {/* Registration Success Message */}
        {registrationSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-brand text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Check Your Email! 📧
            </h3>
            <p className="text-sm text-green-700 mb-3">
              We've sent a verification link to your email address. Please click the link to verify your account.
            </p>
            <p className="text-xs text-green-600">
              The link will expire in 24 hours. Check your spam folder if you don't see it.
            </p>
            <div className="mt-4">
              <Link
                to="/login"
                className="text-sm text-green-600 hover:text-green-800 font-medium underline"
              >
                Go to Login
              </Link>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && !registrationSuccess && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-brand flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Registration Form - Hide after success */}
        {!registrationSuccess && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
              placeholder="John Doe"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
              placeholder="your@email.com"
              required
            />
          </div>

          {/* Date of Birth — DPDP age gating (must be 18+) */}
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">
              Date of Birth
            </label>
            <input
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
              max={todayIso}
              required
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
            />
            {dobIsMinor && (
              <p className="text-xs text-red-500 mt-1">
                You must be 18 or older to create a SkillDelivery account.
              </p>
            )}
            {!dobPresent && (
              <p className="text-xs text-brand-muted2 mt-1">
                Required. SkillDelivery accounts are for users 18 and over.
              </p>
            )}
          </div>

          {/* Password with Strength Indicator */}
          <PasswordInput
            value={formData.password}
            onChange={(value) => setFormData({...formData, password: value})}
            onValidationChange={setPasswordValidation}
            label="Password"
            placeholder="Create a strong password"
            email={formData.email}
            showRequirements={true}
            showMeter={true}
          />

          {/* Confirm Password */}
          <ConfirmPasswordInput
            password={formData.password}
            confirmPassword={formData.confirmPassword}
            onChange={(value) => setFormData({...formData, confirmPassword: value})}
            label="Confirm Password"
            placeholder="Confirm your password"
          />

          {/* V2.0: Account Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-2">
              Account Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {ENTITY_TYPES.map((type) => {
                const Icon = type.icon;
                const selected = formData.entityType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setFormData({...formData, entityType: type.value, subCategoryId: ''})}
                    className={`p-3 border rounded-brand text-left transition-all ${
                      selected
                        ? 'border-brand-cyan bg-brand-cyan/10 ring-2 ring-brand-cyan/30'
                        : 'border-brand-border hover:border-brand-borderAccent'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${selected ? 'text-brand-cyan' : 'text-brand-muted2'}`} />
                      <span className={`text-sm font-medium ${selected ? 'text-brand-cyanDark' : 'text-brand-text'}`}>
                        {type.label}
                      </span>
                    </div>
                    <p className="text-xs text-brand-muted2 mt-1 ml-6">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* V2.0: Organization Name (for non-individual) */}
          {requiresOrgName && (
            <div>
              <label className="block text-sm font-semibold text-brand-text mb-1">
                {formData.entityType === 'organization' ? 'Organization Name' :
                 formData.entityType === 'institute' ? 'Institute Name' : 'University Name'}
              </label>
              <input
                type="text"
                value={formData.organizationName}
                onChange={(e) => setFormData({...formData, organizationName: e.target.value})}
                className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan"
                placeholder={`Enter ${formData.entityType} name`}
                required
              />
            </div>
          )}

          {/* V2.0: Sub-category Selection */}
          {subCategories[formData.entityType]?.length > 0 && (
            <div>
              <label className="block text-sm font-semibold text-brand-text mb-1">
                Category (Optional)
              </label>
              <select
                value={formData.subCategoryId}
                onChange={(e) => setFormData({...formData, subCategoryId: e.target.value})}
                className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan bg-white"
              >
                <option value="">Select a category...</option>
                {subCategories[formData.entityType].map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* User Type */}
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">
              I am a
            </label>
            <select
              value={formData.userType}
              onChange={(e) => setFormData({...formData, userType: e.target.value})}
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan bg-white"
            >
              <option value="learner">Learner</option>
              <option value="mentor">Mentor</option>
            </select>
          </div>

          {/* Vertical / Primary Interest */}
          <div>
            <label className="block text-sm font-semibold text-brand-text mb-1">
              Primary Interest
            </label>
            <select
              value={formData.vertical}
              onChange={(e) => setFormData({...formData, vertical: e.target.value})}
              className="w-full px-4 py-2 border border-brand-border rounded-brand focus:outline-none focus:ring-2 focus:ring-brand-cyan focus:border-brand-cyan bg-white"
            >
              {verticalOptions.map((v) => (
                <option key={v.value} value={v.value}>{v.label}</option>
              ))}
            </select>
          </div>

          {/* V2.0: Terms & Conditions Checkbox */}
          <div className="mt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.tcAccepted}
                onChange={(e) => setFormData({...formData, tcAccepted: e.target.checked})}
                className="mt-1 w-4 h-4 rounded border-brand-border text-brand-cyan focus:ring-brand-cyan"
              />
              <span className="text-sm text-brand-muted">
                I agree to the{' '}
                <a
                  href="https://skilldelivery.co.in/terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-cyan hover:text-brand-cyanDark font-medium inline-flex items-center gap-1"
                >
                  Terms & Conditions
                  <ExternalLink className="w-3 h-3" />
                </a>
                {' '}and{' '}
                <a
                  href="https://skilldelivery.co.in/terms.html#guardrails"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-cyan hover:text-brand-cyanDark font-medium inline-flex items-center gap-1"
                >
                  Platform Use Policy
                  <ExternalLink className="w-3 h-3" />
                </a>
                {tcVersion && (
                  <span className="text-xs text-brand-muted2 ml-1">(v{tcVersion.version})</span>
                )}
              </span>
            </label>
            {!formData.tcAccepted && formData.email && (
              <p className="text-xs text-red-500 mt-1 ml-7">
                You must accept the Terms & Conditions to continue
              </p>
            )}
          </div>

          {/* Captcha — mounted only when server-side kill switch is on. */}
          {needsCaptcha && (
            <div>
              <p className="text-xs text-brand-muted mb-1">Please complete the security check:</p>
              <TurnstileGate
                ref={captchaRef}
                onToken={setCaptchaToken}
                onStatus={setCaptchaStatus}
              />
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isFormValid || captchaBlocks}
            className={`w-full py-2.5 rounded-brand font-semibold flex items-center justify-center gap-2 transition-all duration-200 ${
              isFormValid && !captchaBlocks
                ? 'bg-gradient-to-br from-brand-cyanLight to-brand-blue text-white shadow-brandButton hover:shadow-brandButtonHover hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          {/* Form validation hint */}
          {!isFormValid && formData.password && (
            <p className="text-xs text-brand-muted2 text-center">
              Please complete all fields and ensure password meets requirements
            </p>
          )}
        </form>
        )}

        {!registrationSuccess && (
        <p className="text-center text-sm text-brand-muted mt-6">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-brand-cyan hover:text-brand-cyanDark font-medium"
          >
            Sign in
          </Link>
        </p>
        )}

        {/* Password Policy Info */}
        {!registrationSuccess && (
        <div className="mt-4 p-3 bg-brand-bgOff2 rounded-brand text-xs text-brand-muted">
          <strong className="text-brand-text">Password Requirements:</strong>
          <ul className="mt-1 space-y-0.5 list-disc list-inside">
            <li>At least 12 characters</li>
            <li>Uppercase and lowercase letters</li>
            <li>At least one number</li>
            <li>At least one special character (!@#$%^&*)</li>
          </ul>
        </div>
        )}
      </div>
    </div>
  );
};

export default Register;
